import { ReactNode } from 'react';

import { MuiColors } from '@/types/colors';

/**
 * Параметры для отображения ссылки в алерте с маркером
 */
export type AlertLinkParams = {
  marker: string;
  text: string;
  url: string;
};

/**
 * Разбивает сообщение на части и вставляет ссылки по маркерам
 *
 * @param message - текст сообщения с маркерами (например: "Проверьте {docs} или {help}")
 * @param links - массив ссылок с маркерами, текстом и URL
 * @returns массив React элементов для рендеринга или строка если нет ссылок
 *
 * @example
 * parseMessageWithMarkers(
 *   "Произошла ошибка. Проверьте {docs} или обратитесь в {support}",
 *   [
 *     { marker: '{docs}', text: 'документацию', url: 'https://...' },
 *     { marker: '{support}', text: 'поддержку', url: 'https://...' }
 *   ]
 * )
 */
export const parseMessageWithMarkers = (
  message: string,
  links?: AlertLinkParams[],
): ReactNode[] | string => {
  if (!links || links.length === 0) {
    return message;
  }

  const parts: ReactNode[] = [];
  let remainingMessage = message;
  let partIndex = 0;

  links.forEach((link, linkIndex) => {
    const { marker, text, url } = link;
    const index = remainingMessage.indexOf(marker);

    if (index === -1) {
      return;
    }

    // Текст до маркера
    if (index > 0) {
      parts.push(<span key={`text-${partIndex++}`}>{remainingMessage.substring(0, index)}</span>);
    }

    // Сама ссылка
    parts.push(
      <a
        key={`link-${linkIndex}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: MuiColors.accentColor, textDecoration: 'underline', cursor: 'pointer' }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {text}
      </a>,
    );

    remainingMessage = remainingMessage.substring(index + marker.length);
  });

  if (remainingMessage.length > 0) {
    parts.push(<span key={`text-${partIndex}`}>{remainingMessage}</span>);
  }

  return parts.length > 0 ? parts : message;
};
